import { buildCadenceSchedule } from '../lib/cadenceSchedule';
import type { CycleDesignerFormState, DesignerPath, OrgPathNode } from '../types';

interface CadenceSchedulePreviewProps {
  form: CycleDesignerFormState;
  paths: DesignerPath[];
  orgNodes: OrgPathNode[];
}

/**
 * "The method arrives with its calendar": the level-by-level schedule this
 * cycle design implies, in the anchor mode the designer chose — forward from
 * the start date, or T-minus counted back from the lock date. The same
 * derivation feeds launch (track metadata, level config, first-toss due date).
 */
export function CadenceSchedulePreview({ form, paths, orgNodes }: CadenceSchedulePreviewProps) {
  const schedule = buildCadenceSchedule(form, paths, orgNodes);

  if (schedule.paths.length === 0) return null;

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontFamily: 'var(--mono)', fontSize: '0.65rem', letterSpacing: '0.14em', color: 'var(--text-dim)', textTransform: 'uppercase', marginBottom: 6 }}>
        Cadence Preview · {schedule.anchor === 'lock' ? 'T-minus from end date' : 'Forward from start date'}
      </div>
      <p style={{ fontSize: '0.95rem', color: 'var(--text-ter)', margin: '0 0 12px', lineHeight: 1.45 }}>
        {schedule.anchor === 'lock'
          ? 'This is the calendar this cycle creates, counted back from the lock date (T−0) — each level sees its window, its date, and when the ball must leave the top.'
          : 'This is the calendar this cycle creates — each level sees its window and its date.'}
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {schedule.paths.map((path) => (
          <div
            key={path.pathId}
            style={{ borderLeft: `3px solid ${path.color}`, padding: '12px 14px', background: 'var(--bg-raised)', border: '1px solid var(--rule)', borderLeftWidth: 3, borderLeftColor: path.color }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: '0.95rem', color: 'var(--text-pri)', fontWeight: 500 }}>{path.pathName}</span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {path.stops.map((stop) => (
                <div
                  key={stop.nodeId}
                  style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.4fr) 0.8fr 1fr auto', alignItems: 'baseline', gap: 12, fontSize: '0.9rem' }}
                >
                  <span style={{ color: 'var(--text-sec)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {stop.nodeName}
                  </span>
                  <span style={{ color: 'var(--text-ter)' }}>
                    {stop.level === 0 ? 'launch' : `${stop.windowDays} day${stop.windowDays === 1 ? '' : 's'}`}
                  </span>
                  <span style={{ color: 'var(--text-sec)' }}>
                    {stop.dueDate ?? stop.relativeLabel}
                  </span>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: '0.7rem', letterSpacing: '0.08em', color: 'var(--text-dim)', textAlign: 'right' }}>
                    {stop.tMinusLabel ?? (stop.dueDate ? stop.relativeLabel : '')}
                  </span>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 10, fontSize: '0.85rem', color: 'var(--text-ter)' }}>
              {schedule.daysAvailable !== null
                ? `Descent takes ${path.totalDescentDays} days; cycle window is ${schedule.daysAvailable} days.`
                : `Descent takes ${path.totalDescentDays} days; cycle window not set.`}
              {schedule.anchor === 'lock' && path.publishBy
                ? ` The ball must leave the top by ${path.publishBy}.`
                : ''}
            </div>

            {path.overrun && schedule.daysAvailable !== null && (
              <div style={{ marginTop: 8, padding: '8px 10px', border: '1px solid var(--coral)', color: 'var(--coral)', fontSize: '0.95rem', lineHeight: 1.45 }}>
                {schedule.anchor === 'lock' && path.publishBy
                  ? `To land on the lock date, the ball must leave the top by ${path.publishBy} — before the cycle starts. Tighten windows or move the dates.`
                  : `This path's response windows total ${path.totalDescentDays} days, but the cycle ends in ${schedule.daysAvailable} — tighten windows or move the end date.`}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
